import { AutodeskConstants } from "../../autodeskConstants";
import { IMapToolbar } from "../../interfaces/IMapToolbar";
import { CameraExtension, ICameraExtensionOptions } from "./cameraExtension";
import { ClickExtension, IOnClickOptions } from "./clickExtension";
import { ContextMenuExtension } from "./contextMenuExtension";
import { FloorSelectionExtensionOptions } from "./floorSelector/floorSelectionExtensionOptions";
import { FloorSelectorExtension } from "./floorSelectorExtension";
import { ISelectorExtensionOptions, SelectorExtension } from "./selectorExtension";
import { ToolbarExtension } from "./toolbarExtension";
import { ViewControlExtension, ViewControlExtensionOptions } from "./viewControlExtension";

export class Extensions {
	public constructor(private componentId: string) {}

	public register(): void {
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.Selector), SelectorExtension);
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.Floor), FloorSelectorExtension);
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.ContextMenu), ContextMenuExtension);
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.Toolbar), ToolbarExtension);
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.ViewControl), ViewControlExtension);
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.Camera), CameraExtension);
		Autodesk.Viewing.theExtensionManager.registerExtension(this.getExtensionId(AutodeskConstants.Extensions.Click), ClickExtension);
	}

	public unregister(): void {
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.Selector));
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.Floor));
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.ContextMenu));
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.Toolbar));
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.ViewControl));
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.Camera));
		Autodesk.Viewing.theExtensionManager.unregisterExtension(this.getExtensionId(AutodeskConstants.Extensions.Click));
	}

	public async loadToolbarExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D, mapToolbarOptions: IMapToolbar): Promise<void> {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.Toolbar))) {
			return;
		}

		await viewer.loadExtension(this.getExtensionId(AutodeskConstants.Extensions.Toolbar), mapToolbarOptions);
	}

	public unLoadFloorExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D): void {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.Floor))) {
			viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Floor));
		}
	}

	public async loadFloorExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D, floorOptions: FloorSelectionExtensionOptions): Promise<void> {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.Floor))) {
			viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Floor));
		}
		await viewer.loadExtension(this.getExtensionId(AutodeskConstants.Extensions.Floor), floorOptions);
	}

	public async loadViewControlExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D, viewOptions: ViewControlExtensionOptions): Promise<void> {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.ViewControl))) {
			return;
		}
		await viewer.loadExtension(this.getExtensionId(AutodeskConstants.Extensions.ViewControl), viewOptions);
	}

	public async loadCameraExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D, cameraOptions: ICameraExtensionOptions): Promise<void> {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.Camera))) {
			viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Camera));
		}
		await viewer.loadExtension(this.getExtensionId(AutodeskConstants.Extensions.Camera), cameraOptions);
	}

	public async loadSelectorExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D, selectorOptions: ISelectorExtensionOptions): Promise<void> {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.Selector))) {
			viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Selector));
		}
		await viewer.loadExtension(this.getExtensionId(AutodeskConstants.Extensions.Selector), selectorOptions);
	}

	public async loadContextMenuExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D): Promise<void> {
		if (viewer.isExtensionLoaded(this.getExtensionId(AutodeskConstants.Extensions.ContextMenu))) {
			return;
		}

		await viewer.loadExtension(this.getExtensionId(AutodeskConstants.Extensions.ContextMenu));
	}

	public async loadClickEventsExtensionAsync(viewer: Autodesk.Viewing.GuiViewer3D, clickOptions: IOnClickOptions): Promise<void> {
		const clickExtensionId = this.getExtensionId(AutodeskConstants.Extensions.Click);

		if (viewer.isExtensionLoaded( clickExtensionId)) {
			const extension = viewer.getExtension(clickExtensionId) as ClickExtension;
			extension.clickOptions = clickOptions;
		} else {
			await viewer.loadExtension(clickExtensionId, clickOptions);
		}
	}

	public unLoad(viewer: Autodesk.Viewing.GuiViewer3D): void {
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Selector));
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Floor));
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.ContextMenu));
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Toolbar));
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.ViewControl));
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Camera));
		viewer.unloadExtension(this.getExtensionId(AutodeskConstants.Extensions.Click));
	}

	private getExtensionId(name: string) {
		return `${name}-${this.componentId}`;
	}
}
